/**
 * CountryList widget — rendered inline in chat by the agent.
 *
 * Displays an alphabetically sorted single-column list of countries that have
 * survey data available for a given program.
 */

import { useMemo } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"

interface CountryListProps {
  /** Array of country names to display. */
  countries: string[]
  /** The survey program these countries belong to (e.g. "DHS", "MICS"). */
  program: string
  /** Optional title override for the card header. */
  title?: string
}

/**
 * Renders an alphabetically sorted single-column list of country names.
 *
 * @param props.countries - Array of country name strings.
 * @param props.program - The survey program name (shown in the header).
 * @param props.title - Optional card title override.
 * @returns A Card component with a scrollable list of country names.
 */
export function CountryList({ countries, program, title }: CountryListProps) {
  const sorted = useMemo(() => {
    if (!countries) return []
    return [...countries].sort((a, b) => a.localeCompare(b))
  }, [countries])

  if (!sorted || sorted.length === 0) {
    return (
      <Card className="my-2 border-muted shadow-sm">
        <CardContent className="py-4 text-sm text-muted-foreground">
          No countries found for {program}.
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="my-2 border-primary/20 shadow-sm min-w-64 max-w-full">
      <CardHeader className="pb-2 pt-3 px-4">
        <CardTitle className="text-base font-semibold">
          {title ?? `Countries with ${program} Data`}
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-0.5">
          {sorted.length} countr{sorted.length !== 1 ? "ies" : "y"} available
        </p>
      </CardHeader>
      <CardContent className="px-4 pb-3 pt-0">
        <div className="max-h-64 overflow-y-auto">
          <ul className="flex flex-col gap-y-1 text-sm list-disc list-inside">
            {sorted.map((country) => (
              <li key={country} className="text-foreground">
                {country}
              </li>
            ))}
          </ul>
        </div>
      </CardContent>
    </Card>
  )
}
